import React from "react";
import { formatAmountWithCurrency } from "~/utils";
import { faArrowsRotate, faBox, faGift, faShirt, faTruck } from "@fortawesome/pro-solid-svg-icons";
import { REFUND_LINE_TYPES, REFUND_TRANSACTION_SOURCE, REFUND_TRANSACTION_STATUS, REFUND_TRANSACTION_TYPES, REFUND_TYPES } from "~/constants";
import { REFUND_TYPE_MAP } from "~/components/chips/statusChips/RefundStatusChip.jsx";
import { EntityActionStatusChip } from "~/components/entityAction/EntityActionStatusChip.jsx";
import { CHIP_COLOR_TYPES, CustomChip } from "~/components/chips/CustomChip.jsx";
import CollapsibleReturnPart from "./_components/CollapsibleReturnPart";
import { faCalculator, faCheck, faClock, faExclamationTriangle, faSpinner } from "@fortawesome/pro-regular-svg-icons";
import ItemNameOneLine from "~/components/items/ItemNameOneLine.jsx";
import LineDivider from "rylak-common/src/components/divider/LineDivider";
import { formatReadableDateAndTimeShort } from "rylak-common";
import { getShopEcomGiftCardUrl } from "~/cores/ecom/ecomCore";

const REFUND_LINE_ICONS = {
    [REFUND_LINE_TYPES.ITEM_REFUND]: {
        icon: faShirt,
        label: "Items refund",
    },
    [REFUND_LINE_TYPES.SHIPPING_REFUND]: {
        icon: faTruck,
        label: "Shipping refund",
    },
    [REFUND_LINE_TYPES.EXCHANGE_COST]: {
        icon: faArrowsRotate,
        label: "Exchange cost",
    },
    [REFUND_LINE_TYPES.RETURN_COST]: {
        icon: faTruck,
        label: "Return shipment cost",
    },
    [REFUND_LINE_TYPES.RETURN_SHIPMENT_COST]: {
        icon: faTruck,
        label: "Return shipment cost",
    },
    [REFUND_LINE_TYPES.RETURN_HANDLING_COST]: {
        icon: faBox,
        label: "Return handling cost",
    },
    [REFUND_LINE_TYPES.FAILED_DELIVERY_COST]: {
        icon: faTruck,
        label: "Failed delivery cost",
    },
    [REFUND_LINE_TYPES.GIFT_CARD_ADDITION]: {
        icon: faGift,
        label: "Gift card bonus",
    },
};

function RefundLine({ text, value }) {
    return (
        <>
            <div style={{ fontSize: "12px" }}>{text}</div>
            <div style={{ fontSize: "12px", marginLeft: "auto" }}>{value}</div>
        </>
    );
}

export default function ReturnRefundCard(props) {
    let { appProps, refundTransaction, onUpdate } = props;
    const { transactionType, manualTransactionType, status, source, refundType, currencyCode, comment } = refundTransaction;
    const refundTypeData = REFUND_TYPE_MAP[transactionType || refundType];
    const isGiftCard = transactionType === REFUND_TRANSACTION_TYPES.GIFT_CARD_CREATION || transactionType === REFUND_TRANSACTION_TYPES.GIFT_CARD_REFILL || refundType === REFUND_TYPES.GIFT_CARD;

    const statusData =
        status === REFUND_TRANSACTION_STATUS.PENDING
            ? {
                  label: "Pending",
                  colorType: CHIP_COLOR_TYPES.OK,
                  icon: faClock,
                  tooltipContent: "This refund has been triggered and we are waiting for verification",
              }
            : status === REFUND_TRANSACTION_STATUS.FAILED
              ? {
                    label: "Failed",
                    colorType: CHIP_COLOR_TYPES.ERROR,
                    icon: faExclamationTriangle,
                    tooltipContent: comment || "This refund has failed. Unknown reason",
                }
              : status === REFUND_TRANSACTION_STATUS.AWAITING_EXTERNAL_REFUND
                ? {
                      label: "Awaiting external refund",
                      colorType: CHIP_COLOR_TYPES.OK,
                      icon: faSpinner,
                      tooltipContent: comment || "Awaiting refund to be handled in another system",
                  }
                : source === REFUND_TRANSACTION_SOURCE.CALCULATED
                  ? {
                        label: "Calculated",
                        colorType: CHIP_COLOR_TYPES.ACTION,
                        icon: faCalculator,
                        tooltipContent: "This is the calculated refund amount that the customer will receive when the return has been completed",
                    }
                  : status === REFUND_TRANSACTION_STATUS.SUCCESS
                    ? {
                          label: "Done",
                          colorType: CHIP_COLOR_TYPES.DONE,
                          icon: faCheck,
                          tooltipContent: "This refund has been successfully completed",
                      }
                    : null;

    const needsManualHandling = source === REFUND_TRANSACTION_SOURCE.OTHER && status !== REFUND_TRANSACTION_STATUS.FAILED && !manualTransactionType;

    const totalAmount = formatAmountWithCurrency(refundTransaction.totalAmount, {
        currency: currencyCode,
        maximumFractionDigits: 2,
    });

    const statusChips = [<CustomChip key={"1"} {...statusData} size={"small"} />];
    if (needsManualHandling) {
        statusChips.push(<EntityActionStatusChip style={{ width: "initial" }} size={"small"} key={"2"} appProps={appProps} entity={refundTransaction} onSuccess={onUpdate} />);
    }

    return (
        <CollapsibleReturnPart
            appProps={appProps}
            headerIcon={refundTypeData?.icon}
            headerTitle={refundTypeData?.label}
            headerTooltip={"Refund type: " + refundTypeData?.label}
            headerChip={<CustomChip label={totalAmount} disableTooltip={true} />}
            headerHref={isGiftCard ? getShopEcomGiftCardUrl({ merchantBrand: refundTransaction?.order?.merchantBrand, refundTransaction }) : null}
            statusChips={statusChips}
            dateChip={refundTransaction?.createdAt && <CustomChip tooltipTitle={"Triggered at"} label={formatReadableDateAndTimeShort({ dateTime: refundTransaction?.createdAt })}></CustomChip>}
            optionsEntity={refundTransaction}
            onUpdate={onUpdate}
        >
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "auto auto",
                    gridRowGap: "10px",
                    gridColumnGap: "30px",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                {refundTransaction?.lines?.map((refundLine, index) => {
                    const { type, value } = refundLine;
                    const refundData = REFUND_LINE_ICONS?.[type];

                    let text = refundData?.label;

                    if (type === REFUND_LINE_TYPES.ITEM_REFUND && refundLine?.orderItem) {
                        text = <ItemNameOneLine orderItem={refundLine?.orderItem} />;
                    }

                    return (
                        <React.Fragment key={index}>
                            <RefundLine
                                text={text}
                                value={formatAmountWithCurrency(value, {
                                    currency: currencyCode,
                                    maximumFractionDigits: 2,
                                })}
                            />
                        </React.Fragment>
                    );
                })}
            </div>
            <LineDivider className={"mt1 mb1"}></LineDivider>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "auto auto",
                    gridRowGap: "10px",
                    gridColumnGap: "30px",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                {" "}
                <RefundLine text={<b style={{ color: "black" }}>Total</b>} value={<b>{totalAmount}</b>} />
            </div>
        </CollapsibleReturnPart>
    );
}
